/* eslint-disable @typescript-eslint/no-explicit-any */
import { formatDate } from "@angular/common";
import { inject, Service } from "@angular/core";
import { TranslateService } from "@ngx-translate/core";
import moment from "moment";

import { NumberCommaPipe } from "../../core/pipes/number-comma-pipe";
import { AdaaHelper } from "../../core/utils";
import { Constants } from "../constants/constants";
import {
  AdaaBoolean,
  CellType,
  ImportExportStatus,
  ImportExportType,
  Language,
  PublishedState,
} from "../constants/enums";
import { ObjectStatus, ParameterCatalog } from "../models";
import { CsvExportService } from "./csv-export-service";

@Service()
export class DataTableExportService {
  private _translateService = inject(TranslateService);
  private _csvExportService = inject(CsvExportService);
  private readonly _numberCommaPipe = new NumberCommaPipe();

  /**
   * Export CSV File
   * @param columns
   * @param parameterCatalogs
   * @param data
   */
  public exportTableData(
    tableConfig: string | undefined,
    columns: string[],
    parameterCatalogs: ParameterCatalog[],
    data: any[]
  ): void {
    //remove the actions column
    columns = columns.filter((e) => e !== "actions");

    //reorder the columns in right way
    if (AdaaHelper.getCurrentLang() === Language.English) columns.reverse();

    const rows: string[][] = data.map((row) =>
      columns.map((fieldName) => {
        const value = this._getFormattedData(tableConfig, fieldName, parameterCatalogs, row);
        return value == null ? "" : String(value);
      })
    );

    const header = columns.map((fieldName) => {
      const column = parameterCatalogs.find((e) => e.fieldName === fieldName);
      return column ? (AdaaHelper.getItemValueByToken(column, "name") as string) : fieldName;
    });

    const currentDate = moment();
    const entity = AdaaHelper.getLocalStorage(Constants.localStorageKeys.currentEntity, {
      type: "prop",
      property: AdaaHelper.getFieldLanguage("shortName"),
    }) as string;
    const fileName = `${entity}_${currentDate.format("YYYYMMDD")}_${currentDate.format("HHmmss")}`;

    this._csvExportService.saveCsv({
      fileName,
      headers: header,
      rows,
      delimiter: "\t",
      encoding: "utf-16le",
    });
  }

  private _getFormattedData(
    tableConfig: string | undefined,
    fieldName: string,
    parameterCatalogs: ParameterCatalog[],
    data: any
  ) {
    const col = parameterCatalogs.find((e) => e.fieldName === fieldName);
    const value = data[fieldName];

    switch (col?.fieldType) {
      case CellType.date:
        return AdaaHelper.formatDate(value);
      case CellType.datetime:
        return AdaaHelper.formatDate(value, true);
      case CellType.trueFalse:
        return this._getYesNoLabels(value);
      case CellType.yesNo:
        return this._getYesNoLabels(value === AdaaBoolean.Y);
      case CellType.status:
        return this._getStatusLabel(value);
      case CellType.trend:
        return this._getTrendLabel(value);
      case CellType.metric:
        return this._getMetricLabel(value);
      case CellType.workflow:
        return this._getWorkflowLabel(value);
      case CellType.enabled:
        return this._getEnabledLabel(value === AdaaBoolean.Y);
      case CellType.specialCase:
        return this._getSpecialCaseLabel(col, tableConfig, data);
      default:
        return value;
    }
  }

  private _getYesNoLabels(value: boolean): string {
    return value
      ? this._translateService.instant("common.form.label.yes")
      : this._translateService.instant("common.form.label.no");
  }

  private _getStatusLabel(value: number): string {
    switch (value) {
      case ObjectStatus.ACTIVE:
        return this._translateService.instant("status.1");
      case ObjectStatus.REMOVE:
        return this._translateService.instant("status.2");
      case ObjectStatus.DRAFT:
        return this._translateService.instant("status.3");
      case ObjectStatus.CLOSED:
        return this._translateService.instant("status.4");
      case ObjectStatus.ARCHIVED:
        return this._translateService.instant("status.5");
      case ObjectStatus.PLANNING:
        return this._translateService.instant("status.6");
      case ObjectStatus.IN_REVIEW:
        return this._translateService.instant("status.in_review");
      default:
        return "";
    }
  }

  private _getTrendLabel(value: number): string {
    switch (value) {
      case Constants.TREND.INCREASING:
        return this._translateService.instant("trend.increase");
      case Constants.TREND.DECREASING:
        return this._translateService.instant("trend.decrease");
      case Constants.TREND.ONTARGET:
        return this._translateService.instant("trend.on_target");
      case Constants.TREND.BOUNDED:
        return this._translateService.instant("trend.bounded");
      default:
        return "";
    }
  }

  private _getMetricLabel(value: string): string {
    switch (value) {
      case Constants.METRICS_TYPE.GLOBAL:
        return this._translateService.instant("main_types.G");
      case Constants.METRICS_TYPE.LOCAL:
        return this._translateService.instant("main_types.L");
      case Constants.METRICS_TYPE.LOCAL_ALL:
        return this._translateService.instant("main_types.LA");
      case Constants.METRICS_TYPE.DIMENSION:
        return this._translateService.instant("main_types.D");
      default:
        return "";
    }
  }

  private _getWorkflowLabel(value: number): string {
    switch (value) {
      case Constants.CONSTANT_WORKFLOW_WFTYPES.APPROVAL:
        return this._translateService.instant("workflow.approval");
      case Constants.CONSTANT_WORKFLOW_WFTYPES.REVIEW:
        return this._translateService.instant("workflow.review");
      default:
        return "";
    }
  }

  private _getEnabledLabel(value: boolean): string {
    return value
      ? this._translateService.instant("common.form.label.active")
      : this._translateService.instant("common.form.label.inactive");
  }

  private _getSpecialCaseLabel(col: ParameterCatalog, tableConfig: string | undefined, data: any) {
    const value = data[col.fieldName];

    switch (col.fieldName) {
      case "publishedState":
        return this._getPublishedStateLabel(value);
      case "formula":
        return this._getFormulaLabel(value);
      case "target":
        return this._getTargetLabel(value, data["measurementUnit"]);
      case "annualOptimalPerformance":
        return AdaaHelper.isDefined(value) ? AdaaHelper.percentageValue(value, 2, "") : "";
      case "comments":
      case "categorizedComments":
        return this._getCommentsLabel(data);
      case "attachDate":
        return this._formatDate(value, Constants.yearDate);
      case "periodDate":
      case "syncDate":
      case "conversionPeriod":
        return this._formatDate(value, Constants.shortMonthYearDate);
      case "impextStatus":
        return this._getImportExportStatus(value);
      default:
        return this._getConfigSpecialCaseLabel(col.fieldName, tableConfig, data);
    }
  }

  private _getConfigSpecialCaseLabel(fieldName: string, tableConfig: string | undefined, data: any) {
    const value = data[fieldName];
    const configKeys = Constants.MAIN_TABLE_LIST_CONF_KEY;

    if (tableConfig === configKeys.ENTITY_DATA_SYNC_LOGS && (fieldName === "startDate" || fieldName === "endDate"))
      return this._formatDate(value, Constants.shortMonthYearDate);
    if (tableConfig === configKeys.IMPORT_EXPORT && fieldName === "type") return this._getImportExportType(value);
    if (tableConfig === configKeys.FORECASTED_VALUES_METRICS_EDITOR_LIST && fieldName === "value")
      return this._numberCommaPipe.transform(value);
    if (tableConfig === configKeys.DATA_ENTRY_METRICS_LIST && fieldName === "actual")
      return this._numberCommaPipe.transform(value);
    if (tableConfig === configKeys.EMPLOYEE_PULSE_LOGS && fieldName === "status")
      return this._translateService.instant(`employee_pulse.manage.status.${value}`);
    if (tableConfig === configKeys.M1_PROJECTS_LIST && fieldName === "projectStatus")
      return this._getProjectStatusLabel(value);
    if (tableConfig === configKeys.PROJECTS_LIST && fieldName === "projectStatus")
      return this._getProjectStatusLabel(value);
    if (tableConfig !== configKeys.DATA_ENTRY_MONITORING_METRICS_LIST) return value;

    switch (fieldName) {
      case "actual":
      case "actualSynced":
        return this._numberCommaPipe.transform(value);
      case "manualEntry":
        return data["actualOverride"] ? this._numberCommaPipe.transform(data["actual"]) : "-";
      default:
        return value;
    }
  }

  private _getPublishedStateLabel(value: number): string {
    return value === PublishedState.notPublished
      ? this._translateService.instant("non_published")
      : this._translateService.instant("published");
  }

  private _getFormulaLabel(value: string): string {
    return value === "NOFORMULAV" ? this._translateService.instant("kpi.NOFORMULAV") : value;
  }

  private _getImportExportStatus(value: string) {
    switch (value) {
      case ImportExportStatus.DONE:
        return this._translateService.instant("importExport.file_status.done");
      case ImportExportStatus.FAIL:
        return this._translateService.instant("importExport.file_status.fail");
      case ImportExportStatus.PENDING:
        return this._translateService.instant("importExport.file_status.pending");
      case ImportExportStatus.SUCCESS:
        return this._translateService.instant("importExport.file_status.success");
      default:
        return "";
    }
  }

  private _getImportExportType(value: string) {
    switch (value) {
      case ImportExportType.IMPORT:
        return this._translateService.instant("importExport.operation_type.import");
      case ImportExportType.EXPORT:
        return this._translateService.instant("importExport.operation_type.export");
      default:
        return "";
    }
  }

  private _getTargetLabel(value: number, measurementUnit: number) {
    if (measurementUnit !== Constants.MEASUREMENT.PERCENTAGE) return AdaaHelper.roundValue(value, 2, "", true);
    if (!AdaaHelper.isDefined(value)) return "";

    return AdaaHelper.percentageValue(value, 2, "", "%", true);
  }

  private _getCommentsLabel(data: any) {
    const categorized = ((data["categorizedComments"] ?? []) as { comments: string }[])
      .filter((item) => this._hasComment(item.comments))
      .map((item) => `${item.comments} (${AdaaHelper.getItemValueByToken(item, "categoryName")})`);

    if (this._hasComment(data["comments"])) categorized.push(data["comments"]);

    return categorized.join("\n");
  }

  private _hasComment(comments: string | undefined) {
    return AdaaHelper.isDefinedAndNotEmpty(comments?.trim());
  }

  private _getProjectStatusLabel(value: string) {
    if (!value) return "";
    return this._translateService.instant(`initiatives.project_status.${value.toLocaleLowerCase()}`);
  }

  private _formatDate(value: number, format: string) {
    return value ? formatDate(value, format, "en-US", Constants.uaeTimezone) : "";
  }
}
